import { Input, Button, Icons, Text } from '@oc-labs/wt-storybook';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import useAuth from '../../../../hooks/useAuth';
import { MailIcon } from 'lucide-react';
import TurnstileCaptcha from '../../../../components/TurnstileCaptcha';
import LinkParams from '../../../../components/LinkParams';

export default function AuthPasswordRecoverView() {
  const [email, setEmail] = useState('');
  const [lastRequestTime, setLastRequestTime] = useState(Number(localStorage.getItem('lastPasswordRecoverRequest')) || 0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);
  const { client } = useAuth();

  const isCooldown = Number(Date.now() - lastRequestTime) < 60000;

  const handleRecover = async () => {
    toast.promise(
      client.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/auth/password/reset`,
        fetchOptions: { 
          headers: { 
              "x-captcha-response": captcha || "",
          }, 
        } 
      }),
      {
        loading: 'Sending recovery email...',
        success: 'Recovery email sent.',
        error: 'Error sending recovery email.',
      }
    );
    const currentTime = Date.now();
    localStorage.setItem('lastPasswordRecoverRequest', currentTime.toString());
    setLastRequestTime(currentTime);
    setCaptcha(null);
    captchaRef.current?.reset();
  };

  useEffect(() => {
    let interval;
    if (isCooldown) {
      interval = setInterval(() => {
        const elapsed = Date.now() - lastRequestTime;
        const remaining = Math.max(0, 60000 - elapsed);
        setSecondsLeft(Math.ceil(remaining / 1000));
      }, 500);
    } else {
      setSecondsLeft(0);
    }
    return () => clearInterval(interval);
  }, [isCooldown, lastRequestTime]);

  return (<div className='p-4 flex flex-col'>
    <div className="flex items-center space-x-2 mb-8 mt-4">
      <LinkParams className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center" to={`/auth/login${window.location.search}`}>
        <Icons name="chevron_left" size={16} />
      </LinkParams>

      <div className="text-lg font-semibold">
        <Text>Recover Password</Text>
      </div>
    </div>

    {isCooldown && (
      <div className="mb-4 p-3 bg-blue-100/40 rounded-lg text-center flex flex-col items-center">
        <MailIcon className="w-5 h-5 text-blue-600 mb-2" />
        <Text className='text-sm'>Password recovery email has been sent.</Text>
      </div>
    )}
    
    <div className="mb-8">
      <Input label={<Text>Email</Text>} placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} type='text' />
    </div>

    <TurnstileCaptcha 
      ref={captchaRef}
      onSuccess={(token) => setCaptcha(token)} 
      onError={() => console.error('Captcha error')}
    />

    <div className="mt-auto mb-4">
      <Button type="primary" size="large" className="w-full" onClick={handleRecover} disabled={!email || isCooldown}>
        Recover Password {isCooldown && `(${secondsLeft}s)`}
      </Button>
    </div>
  </div>);
}

